import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import {
  Sparkles,
  Send,
  Loader2,
  Trash2,
  AlertTriangle,
  FlaskConical,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAskAiStore } from '@/store/askAiStore';
import { useAskAiMutation } from '@/api/hooks/useAskAi';
import { useMapActionsStore } from '@/store/mapActionsStore';
import type { AskAiQueryResponse, AskAiTurn } from '@/types/askAi';
import { AskAiResultTable } from './AskAiResultTable';
import { ChartDispatcher } from './charts/ChartDispatcher';
import { FindingCard } from './FindingCard';
import { DetectiveEnvelope } from './DetectiveEnvelope';
import { FeedbackFooter } from './FeedbackFooter';

const MAX_INPUT_CHARS = 1000;
const COUNTER_THRESHOLD = 800;

function nextId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function isValidChartHint(resp: AskAiQueryResponse): boolean {
  if (!resp.is_chartable || !resp.chart_hint || resp.rows.length === 0) return false;
  const hint = resp.chart_hint;
  if (hint.template_id) return true;
  return !!hint.x && !!hint.y && resp.columns.includes(hint.x) && resp.columns.includes(hint.y);
}

function AssistantBubble({ resp }: { resp: AskAiQueryResponse }) {
  const [showTable, setShowTable] = useState(false);
  const chartable = isValidChartHint(resp);

  return (
    <div className="space-y-3 rounded-lg bg-muted px-3 py-2 text-sm">
      {resp.error ? (
        <p className="flex items-start gap-2 text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{resp.error}</span>
        </p>
      ) : (
        <>
          {resp.detective && <DetectiveEnvelope env={resp.detective} />}

          {resp.explanation && (
            <p className="leading-relaxed">{resp.explanation}</p>
          )}

          {resp.findings && resp.findings.length > 0 && (
            <div className="space-y-2">
              {resp.findings.map((f, i) => (
                <FindingCard key={`${f.kind}-${i}`} finding={f} />
              ))}
            </div>
          )}

          <details className="group rounded border bg-background/60 px-2 py-1 text-xs">
            <summary className="cursor-pointer select-none text-muted-foreground hover:text-foreground">
              View SQL
            </summary>
            <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded bg-background p-2 font-mono text-[11px] leading-relaxed">
              {resp.sql}
            </pre>
          </details>

          {chartable && resp.chart_hint && (
            <>
              <ChartDispatcher
                columns={resp.columns}
                rows={resp.rows}
                hint={resp.chart_hint}
              />
              <button
                type="button"
                onClick={() => setShowTable((v) => !v)}
                className="text-xs text-primary hover:underline"
              >
                {showTable ? 'Hide data' : 'View data'}
              </button>
              {showTable && (
                <AskAiResultTable columns={resp.columns} rows={resp.rows} />
              )}
            </>
          )}

          {!chartable && (
            <AskAiResultTable columns={resp.columns} rows={resp.rows} />
          )}

          <p className="text-[11px] text-muted-foreground">
            {resp.row_count} rows · {resp.execution_time_ms} ms
            {resp.truncated && ' · capped at 1000'}
          </p>

          {resp.detective?.audit_id && resp.detective.verdict !== 'refuse' && (
            <FeedbackFooter auditId={resp.detective.audit_id} />
          )}
        </>
      )}
    </div>
  );
}

function TurnView({ turn }: { turn: AskAiTurn }) {
  if (turn.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground">
          {turn.question}
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-start">
      <div className="w-full max-w-full">
        <AssistantBubble resp={turn.response} />
      </div>
    </div>
  );
}

export function AskAiSheet() {
  const isOpen = useAskAiStore((s) => s.isOpen);
  const close = useAskAiStore((s) => s.close);
  const turns = useAskAiStore((s) => s.turns);
  const history = useAskAiStore((s) => s.history);
  const appendTurn = useAskAiStore((s) => s.appendTurn);
  const pushHistory = useAskAiStore((s) => s.pushHistory);
  const clearConversation = useAskAiStore((s) => s.clearConversation);
  const pendingQuestion = useAskAiStore((s) => s.pendingQuestion);
  const clearPendingQuestion = useAskAiStore((s) => s.clearPendingQuestion);

  const [input, setInput] = useState('');
  const [networkError, setNetworkError] = useState<string | null>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const mutation = useAskAiMutation();
  const inFlight = mutation.isPending;
  const navigate = useNavigate();
  const location = useLocation();
  const pushMapActions = useMapActionsStore((s) => s.pushActions);

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [turns, inFlight]);

  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    const lineHeight = 20;
    const maxHeight = lineHeight * 4 + 16;
    ta.style.height = `${Math.min(ta.scrollHeight, maxHeight)}px`;
  }, [input]);

  const trimmed = input.trim();
  const canSend = !!trimmed && !inFlight && trimmed.length <= MAX_INPUT_CHARS;

  // `raw` lets a caller (guided-tour chip) send a question that was never typed
  // into the textarea; with no argument it sends whatever is in the box.
  const submit = async (raw?: string) => {
    const question = (raw ?? input).trim();
    if (!question || inFlight || question.length > MAX_INPUT_CHARS) return;
    setInput('');
    setNetworkError(null);

    appendTurn({ id: nextId(), role: 'user', question });
    pushHistory({ role: 'user', content: question });

    try {
      const resp = await mutation.mutateAsync({ question, history });
      appendTurn({ id: nextId(), role: 'assistant', response: resp });
      pushHistory({
        role: 'assistant',
        content: resp.error ?? resp.explanation ?? '',
      });
      if (resp.map_actions && resp.map_actions.length > 0) {
        pushMapActions(resp.map_actions);
        if (!location.pathname.startsWith('/maps')) {
          navigate('/maps');
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Request failed.';
      setNetworkError(msg);
    }
  };

  // Consume a question queued by the guided-tour chips. Cleared first so a
  // failed send can't loop, and only auto-sent when nothing is in flight —
  // otherwise it lands in the box for the presenter to send by hand.
  useEffect(() => {
    if (!pendingQuestion) return;
    clearPendingQuestion();
    if (inFlight) {
      setInput(pendingQuestion);
      return;
    }
    void submit(pendingQuestion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingQuestion]);

  const onFormSubmit = (e: FormEvent) => {
    e.preventDefault();
    void submit();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void submit();
    }
  };

  const onChange = (v: string) => {
    if (v.length > MAX_INPUT_CHARS) {
      setInput(v.slice(0, MAX_INPUT_CHARS));
      return;
    }
    setInput(v);
  };

  const showCounter = input.length >= COUNTER_THRESHOLD;

  return (
    <Sheet open={isOpen} onOpenChange={(v) => (v ? null : close())}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[480px]"
      >
        <SheetHeader className="border-b px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex flex-col gap-0.5">
              <SheetTitle className="flex items-center gap-2 text-base">
                <Sparkles className="h-4 w-4 text-primary" />
                Ask AI
              </SheetTitle>
              <SheetDescription className="text-xs">
                Natural-language SQL over the fleet warehouse.
              </SheetDescription>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {/* The credibility escape hatch: "how did you work that out?" is
                  one click away from any answer the Detective gives. */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  close();
                  navigate('/methodology');
                }}
                className="h-7 gap-1 px-2 text-xs"
                title="How the Detective works — every method, named"
              >
                <FlaskConical className="h-3 w-3" />
                Methods
              </Button>
              {turns.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    clearConversation();
                    setNetworkError(null);
                  }}
                  className="h-7 gap-1 px-2 text-xs"
                >
                  <Trash2 className="h-3 w-3" />
                  Clear
                </Button>
              )}
            </div>
          </div>
        </SheetHeader>

        <div
          ref={threadRef}
          className="flex-1 space-y-3 overflow-y-auto px-4 py-4"
        >
          {turns.length === 0 && !inFlight && (
            <div className="flex h-full flex-col items-center justify-center text-center text-sm text-muted-foreground">
              <Sparkles className="mb-2 h-6 w-6 text-primary/60" />
              <p className="max-w-xs">
                Ask a question in plain English. The model writes SQL,
                validates it against an allow-list, and runs it for you.
              </p>
            </div>
          )}

          {turns.map((t) => (
            <TurnView key={t.id} turn={t} />
          ))}

          {inFlight && (
            <div className="flex justify-start">
              <div className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Thinking…
              </div>
            </div>
          )}

          {networkError && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {networkError}
            </div>
          )}
        </div>

        <form
          onSubmit={onFormSubmit}
          className="border-t bg-background px-4 py-3"
        >
          <div className="flex items-end gap-2">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Ask a question…"
              rows={1}
              className={cn(
                'flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm leading-5',
                'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              )}
            />
            <Button
              type="submit"
              size="icon"
              disabled={!canSend}
              aria-label="Send"
            >
              {inFlight ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
          <div className="mt-1 flex items-center justify-between text-[10px] text-muted-foreground">
            <span>Press Enter to send, Shift+Enter for newline.</span>
            {showCounter && (
              <span
                className={cn(
                  input.length >= MAX_INPUT_CHARS && 'text-destructive',
                )}
              >
                {input.length}/{MAX_INPUT_CHARS}
              </span>
            )}
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

export default AskAiSheet;
